import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@carbon/react';
import { definePage, defineResource, ListQuery } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import ImagesViewer from 'src/components/Base/ImagesViewer';
import MenuItemForm from 'src/components/Menu/MenuItemForm';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import {
  tenantMenuItemCreate,
  tenantMenuItemDelete,
  tenantMenuItemUpdate,
  tenantMenuItems,
  tenantMenu,
} from 'src/services/apis/tenantRequests';
import { RestaurantScope } from 'src/services/apis/tenantRequests';
import { MenuItemEntity } from 'src/services/apis/types';
import { getImageUrl } from 'src/utils/images';
import { QUERIES } from 'src/utils/react-query';

/**
 * The MenuItems page config, as a hook for the translated photo links and the photo viewer
 * state. The menu used to be a ComboBox in the toolbar with its own `menu` URL param and a
 * manual refetch; as a `filterable` reference chip it lives in the Filters popover like
 * every other page's filters, rides the crud layer's own URL keys, and refetches through
 * the query key (the same migration Item Variants' branch filter went through).
 */
export const useMenuItemsPage = () => {
  const { t } = useTranslation();
  const currentRestaurant = useCurrentRestaurant();

  const menuItems = useMemo(
    () =>
      defineResource({
        key: QUERIES.MENU_ITEMS,
        // The hand-written hook sent no limit, so the server (alwaysPaginate, no configured
        // take) returned every row in one shot; 1000 is the practical match — the same
        // reach the pilot used for an effectively-unbounded list.
        pageSize: 1000,
        api: {
          list: (scope: RestaurantScope, query?: ListQuery) => tenantMenuItems(scope, query),
          create: tenantMenuItemCreate,
          update: tenantMenuItemUpdate,
          remove: tenantMenuItemDelete,
        },
        // The hand-written page filtered name/sku in memory over the fetched set.
        search: 'client',
        searchFields: ['name', 'sku'],
        // A plain server column (menuId||$eq), so it compiles straight into the list request;
        // the menuId column's reference display loads the same menus.
        filterable: [
          {
            field: 'menuId',
            labelKey: 'common.menu',
            type: 'reference',
            load: tenantMenu,
            optionLabel: (item: any) => item.name,
          },
          { field: 'available', labelKey: 'common.available', type: 'boolean' },
        ],
      }),
    []
  );

  const [viewingMenuItemPhotos, setViewingMenuItemPhotos] = useState<MenuItemEntity[]>([]);

  const page = {
    ...definePage(menuItems, {
      titleKey: 'titles.menuItemsPage',
      descriptionKey: 'common.items',
      createLabelKey: 'actions.createNewMenuItem',
      updateLabelKey: 'actions.updateMenuItem',
      deleteHeadingKey: 'messages.areYouSureToDeleteThisMenuItem',
      columns: [
        // The column a user edits an item by must survive every prefs reconciliation.
        { key: 'name', headerKey: 'common.name', sortable: true, alwaysVisible: true },
        { key: 'sku', headerKey: 'common.sku' },
        {
          key: 'menuId',
          headerKey: 'common.menu',
          display: {
            kind: 'reference',
            load: tenantMenu,
            optionLabel: (item: any) => item.name,
          },
        },
        // currency renders via the portal's useMoney formatter — the same one the hand-written
        // cell used, minus the empty-string gap it left whenever price was missing.
        { key: 'price', headerKey: 'common.price', display: { kind: 'currency' } },
        {
          key: 'photos',
          headerKey: 'common.photo',
          render: (row: MenuItemEntity) =>
            row.photos?.length ? (
              <Link onClick={() => setViewingMenuItemPhotos([row])}>{row.photos[0]}</Link>
            ) : (
              t('messages.noPhotos')
            ),
        },
        { key: 'order', headerKey: 'common.sort' },
        // The hand-written cell was a dead per-row Toggle (no onToggle handler, so it never
        // changed anything) — the same yes/no copy as static text, like the pilot's menus page.
        { key: 'available', headerKey: 'common.available', display: { kind: 'boolean' } },
      ],
      form: MenuItemForm,
      emptyState: <EmptyState />,
      toPayload: (values: any, params) => ({
        ...values,
        restaurantId: params.restaurantId,
        tags: values.tags?.split(',').map((tag: string) => tag.trim()) || [],
        photos: values.photo ? [values.photo] : [],
        price: Number(values.price),
        order: parseInt(values.order, 10),
        blurhashPhotos: values.photoBlurhash ? [values.photoBlurhash] : [],
      }),
      useTitleVars: () => ({ restaurantName: currentRestaurant?.name }),
    }),
  };

  const imagesViewer = (
    <ImagesViewer
      images={viewingMenuItemPhotos.map((item) => ({
        src: getImageUrl(item.photos[0])!,
        caption: `${currentRestaurant?.name} - ${item.name} ${t('common.photos')}`,
      }))}
      onClose={() => setViewingMenuItemPhotos([])}
    />
  );

  return { page, imagesViewer };
};
